import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { Form, Input, Button, Typography, Card, message, Alert } from 'antd';
import { UserOutlined, LockOutlined, EyeInvisibleOutlined, EyeTwoTone, KeyOutlined } from '@ant-design/icons';
import LandingLayout from '../components/Layout/LandingLayout';
import { api } from '../api';

const { Title, Paragraph } = Typography;

// Password validation function similar to registration
const validatePassword = (password: string, t: any): string | null => {
  if (password.length < 8) {
    return t('forgotPassword.passwordRequirements');
  }
  if (!/(?=.*[a-z])/.test(password)) {
    return t('forgotPassword.passwordRequirements');
  }
  if (!/(?=.*[A-Z])/.test(password)) {
    return t('forgotPassword.passwordRequirements');
  }
  if (!/(?=.*\d)/.test(password)) {
    return t('forgotPassword.passwordRequirements');
  }
  return null;
};

const ForgotPassword: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [loading, setLoading] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);

  const handleEmailSubmit = async (values: { email: string }) => {
    setLoading(true);
    
    try {
      await api.post(`/auth/password-recovery/${values.email}`);
      setEmail(values.email);
      message.success(t('forgotPassword.emailSent'));
      setStep('code');
    } catch (err: any) {
      if (err.response?.status === 404) {
        message.error(t('forgotPassword.userNotFound'));
      } else {
        message.error(t('forgotPassword.emailError'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = async (values: { code: string; newPassword: string; confirmPassword: string }) => {
    // Validate passwords match
    if (values.newPassword !== values.confirmPassword) {
      message.error(t('forgotPassword.passwordsDoNotMatch'));
      return;
    }

    // Validate password strength
    const passwordError = validatePassword(values.newPassword, t);
    if (passwordError) {
      message.error(passwordError);
      return;
    }

    setLoading(true);
    
    try {
      await api.post('/auth/reset-password/', {
        code: values.code.toUpperCase(),
        new_password: values.newPassword,
      });
      
      message.success(t('forgotPassword.passwordResetSuccess'));
      
      // Redirect to login page after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      
    } catch (err: any) {
      if (err.response?.status === 400) {
        message.error(t('forgotPassword.codeError'));
      } else {
        message.error(t('forgotPassword.resetError'));
      }
    } finally {
      setLoading(false);
    }
  };

  const formatCode = (value: string) => {
    // Remove all non-alphanumeric characters and convert to uppercase
    const cleaned = value.replace(/[^A-Z0-9]/gi, '').toUpperCase();
    
    // Insert dash after 4 characters
    if (cleaned.length > 4) {
      return cleaned.slice(0, 4) + '-' + cleaned.slice(4, 8);
    }
    return cleaned;
  };

  const formCardStyle = {
    width: '100%',
    padding: '2px',
    background: 'linear-gradient(135deg, #1a56db 0%, #1890ff 100%)',
    borderRadius: '16px',
    boxShadow: '0 20px 40px rgba(26, 86, 219, 0.15)',
    border: 'none',
    overflow: 'hidden'
  };

  const innerFormStyle = {
    background: 'white',
    borderRadius: '12px',
    padding: '40px 20px',
    position: 'relative' as const,
    overflow: 'hidden'
  };

  const fieldStyle = (fieldName: string) => ({
    height: '52px',
    fontSize: '16px',
    borderRadius: '12px',
    border: focusedField === fieldName ? '2px solid #1a56db' : '2px solid #e8e8e8',
    boxShadow: focusedField === fieldName ? '0 0 0 3px rgba(26, 86, 219, 0.1)' : 'none',
    transition: 'all 0.3s ease',
    background: focusedField === fieldName ? '#f6f9ff' : 'white'
  });

  const buttonStyle = {
    height: '52px',
    fontSize: '16px',
    fontWeight: 'bold',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #1a56db 0%, #1890ff 100%)',
    border: 'none',
    boxShadow: '0 8px 16px rgba(26, 86, 219, 0.3)',
    transition: 'all 0.3s ease',
    position: 'relative' as const,
    overflow: 'hidden'
  };

  return (
    <LandingLayout>
      <div style={{ maxWidth: 500, margin: '0 auto', padding: '40px 20px' }}>
        <Card style={formCardStyle}>
          <div style={innerFormStyle}>
            {/* Декоративные элементы */}
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '100px',
              height: '100px',
              background: 'linear-gradient(135deg, rgba(26, 86, 219, 0.1) 0%, rgba(24, 144, 255, 0.1) 100%)',
              borderRadius: '50%',
              zIndex: 0
            }} />
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '60px',
              height: '60px',
              background: 'linear-gradient(135deg, rgba(26, 86, 219, 0.05) 0%, rgba(24, 144, 255, 0.05) 100%)',
              borderRadius: '50%',
              zIndex: 0
            }} />
            
            <div style={{ position: 'relative', zIndex: 1 }}>
              <Title level={2} style={{ 
                textAlign: 'center', 
                marginBottom: 20, 
                fontSize: '28px',
                background: 'linear-gradient(135deg, #1a56db 0%, #1890ff 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontWeight: 'bold'
              }}>
                {t('forgotPassword.title')}
              </Title>

              <Paragraph style={{ 
                textAlign: 'center', 
                marginBottom: 30,
                color: '#666',
                fontSize: '14px'
              }}>
                {step === 'email' 
                  ? t('forgotPassword.enterEmailDescription')
                  : t('forgotPassword.enterCodeDescription')
                }
              </Paragraph>

              {step === 'email' && (
                <Form
                  name="forgot-password-email"
                  onFinish={handleEmailSubmit}
                  layout="vertical"
                  size="large"
                  style={{ width: '100%' }}
                >
                  <Form.Item
                    name="email"
                    rules={[
                      { required: true, message: t('forgotPassword.emailRequired') },
                      { type: 'email', message: t('forgotPassword.emailInvalid') }
                    ]}
                    style={{ marginBottom: '24px' }}
                  >
                    <Input
                      prefix={<UserOutlined style={{ fontSize: '18px', color: '#1a56db' }} />}
                      placeholder={t('forgotPassword.email')}
                      size="large"
                      style={fieldStyle('email')}
                      onFocus={() => setFocusedField('email')}
                      onBlur={() => setFocusedField(null)}
                    />
                  </Form.Item>

                  <Form.Item style={{ marginBottom: '24px' }}>
                    <Button 
                      type="primary" 
                      htmlType="submit" 
                      block 
                      size="large"
                      loading={loading}
                      style={buttonStyle}
                    >
                      {loading ? t('common.loading') : t('forgotPassword.sendCode')}
                    </Button>
                  </Form.Item>
                </Form>
              )}

              {step === 'code' && (
                <Form
                  name="forgot-password-reset"
                  onFinish={handleCodeSubmit}
                  layout="vertical"
                  size="large"
                  style={{ width: '100%' }}
                >
                  <Form.Item
                    name="code"
                    rules={[
                      { required: true, message: t('forgotPassword.codeRequired') }
                    ]}
                    style={{ marginBottom: '24px' }}
                  >
                    <Input
                      prefix={<KeyOutlined style={{ fontSize: '18px', color: '#1a56db' }} />}
                      placeholder="XXXX-XXXX"
                      size="large"
                      style={fieldStyle('code')}
                      onFocus={() => setFocusedField('code')}
                      onBlur={() => setFocusedField(null)}
                      onChange={(e) => {
                        const formatted = formatCode(e.target.value);
                        e.target.value = formatted;
                      }}
                      maxLength={9}
                    />
                  </Form.Item>

                  <Form.Item
                    name="newPassword"
                    rules={[
                      { required: true, message: t('forgotPassword.passwordRequired') }
                    ]}
                    style={{ marginBottom: '24px' }}
                  >
                    <Input.Password
                      prefix={<LockOutlined style={{ fontSize: '18px', color: '#1a56db' }} />}
                      placeholder={t('forgotPassword.newPassword')}
                      size="large"
                      style={fieldStyle('newPassword')}
                      onFocus={() => setFocusedField('newPassword')}
                      onBlur={() => setFocusedField(null)}
                      iconRender={(visible) => (visible ? <EyeTwoTone twoToneColor="#1a56db" /> : <EyeInvisibleOutlined style={{ color: '#1a56db' }} />)}
                    />
                  </Form.Item>

                  <Form.Item
                    name="confirmPassword"
                    rules={[
                      { required: true, message: t('forgotPassword.confirmPasswordRequired') }
                    ]}
                    style={{ marginBottom: '24px' }}
                  >
                    <Input.Password
                      prefix={<LockOutlined style={{ fontSize: '18px', color: '#1a56db' }} />}
                      placeholder={t('forgotPassword.confirmPassword')}
                      size="large"
                      style={fieldStyle('confirmPassword')}
                      onFocus={() => setFocusedField('confirmPassword')}
                      onBlur={() => setFocusedField(null)}
                      iconRender={(visible) => (visible ? <EyeTwoTone twoToneColor="#1a56db" /> : <EyeInvisibleOutlined style={{ color: '#1a56db' }} />)}
                    />
                  </Form.Item>

                  <Alert
                    message={t('forgotPassword.passwordRequirements')}
                    type="info"
                    showIcon
                    style={{ marginBottom: '24px', fontSize: '12px' }}
                  />

                  <Form.Item style={{ marginBottom: '24px' }}>
                    <Button 
                      type="primary" 
                      htmlType="submit" 
                      block 
                      size="large"
                      loading={loading}
                      style={buttonStyle}
                    >
                      {loading ? t('common.loading') : t('forgotPassword.resetPassword')}
                    </Button>
                  </Form.Item>
                </Form>
              )}

              <div style={{ 
                textAlign: 'center', 
                fontSize: '16px',
                padding: '16px 0',
                borderTop: '1px solid #e8e8e8',
                marginTop: '16px'
              }}>
                <Link 
                  to="/login"
                  style={{ 
                    fontSize: '14px',
                    color: '#1890ff',
                    textDecoration: 'none'
                  }}
                >
                  {t('forgotPassword.backToLogin')}
                </Link>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </LandingLayout>
  );
};

export default ForgotPassword; 